import { useMemo } from "react";
import { Search, ShieldAlert } from "lucide-react";

import type {
  InvestigationCell,
  InvestigationCellStatus,
  InvestigationPlan,
} from "../../domain/types";
import { useDebateStore } from "../../store/debateStore";
import { cn } from "../common/cn";

/**
 * Kapsam-Güdümlü Soruşturma — `investigation.plan` defterinin verdict
 * sekmesi görünümü + canlı transcript üstü kompakt çubuk
 * (InvestigationLiveBar). `CoveragePanel`/`CoverageLiveBar` ile AYNI
 * veri-önceliği sözleşmesi: verdict JSON'undaki `investigation_plan`
 * (final) > canlı SSE snapshot'ı (son). İkisi de yoksa sekme HİÇ
 * render edilmez (VerdictPanel'in `visibleTabs` filtresi).
 */

const STATUS_META: Record<
  InvestigationCellStatus,
  { glyph: string; label: string; text: string; chip: string }
> = {
  covered: {
    glyph: "✓",
    label: "Kapatıldı",
    text: "text-emerald-300",
    chip: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  },
  partial: {
    glyph: "~",
    label: "Kısmi",
    text: "text-amber-300",
    chip: "border-amber-400/45 bg-amber-500/10 text-amber-200",
  },
  open: {
    glyph: "○",
    label: "Açık",
    text: "text-rose-300",
    chip: "border-rose-500/45 bg-rose-500/10 text-rose-200",
  },
  no_evidence: {
    glyph: "∅",
    label: "Kanıt Yok",
    text: "text-fg-subtle",
    chip: "border-white/[0.12] bg-surface-2/60 text-fg-subtle",
  },
};

function scorePct(score: number): number {
  return Math.round(Math.max(0, Math.min(1, score)) * 100);
}

interface EntityGroup {
  entity: string;
  entityType: string;
  mustCover: boolean;
  cells: InvestigationCell[];
}

/** Hücreleri varlığa göre grupla — zorunlu varlıklar önce, sonra en
 * yüksek önceliğe göre (`CoverageDrivenStrategy` seçim sırasıyla aynı
 * ilke: must_cover > priority). */
function groupByEntity(cells: InvestigationCell[]): EntityGroup[] {
  const map = new Map<string, EntityGroup>();
  for (const c of cells) {
    const key = c.entity.toLocaleLowerCase("tr-TR");
    const existing = map.get(key);
    if (existing) {
      existing.cells.push(c);
      existing.mustCover = existing.mustCover || c.mustCover;
    } else {
      map.set(key, {
        entity: c.entity,
        entityType: c.entityType,
        mustCover: c.mustCover,
        cells: [c],
      });
    }
  }
  const groups = Array.from(map.values());
  groups.sort((a, b) => {
    if (a.mustCover !== b.mustCover) return a.mustCover ? -1 : 1;
    const bestA = Math.max(...a.cells.map((c) => c.priority));
    const bestB = Math.max(...b.cells.map((c) => c.priority));
    return bestB - bestA;
  });
  return groups;
}

function aggregateStatus(cells: InvestigationCell[]): InvestigationCellStatus {
  if (cells.some((c) => c.status === "open")) return "open";
  if (cells.some((c) => c.status === "partial")) return "partial";
  if (cells.every((c) => c.status === "no_evidence")) return "no_evidence";
  return "covered";
}

function EntityCard({ group }: { group: EntityGroup }) {
  const agg = aggregateStatus(group.cells);
  const meta = STATUS_META[agg];
  const openQuestions = group.cells.flatMap((c) => c.openQuestions).slice(0, 2);
  const citationCount = new Set(group.cells.flatMap((c) => c.citationUrls)).size;

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-white/[0.05] bg-surface-1/40 px-3 py-2.5">
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "grid h-5 w-5 flex-none place-items-center rounded-full border text-[12px] font-bold leading-none",
            meta.chip,
          )}
          title={meta.label}
          aria-label={meta.label}
        >
          {meta.glyph}
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium leading-snug text-fg-strong">
            {group.entity}
            {group.mustCover ? (
              <span
                className="rounded-full border border-accent-400/40 bg-accent-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-300"
                title="Soruda açıkça adı geçen bir aktör — atlanamaz."
              >
                Zorunlu
              </span>
            ) : null}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-fg-subtle">
            <span className="rounded-full border border-white/[0.08] bg-surface-2/60 px-1.5 py-0.5 uppercase tracking-wider">
              {group.entityType}
            </span>
            <span className={cn("font-medium uppercase tracking-wider", meta.text)}>
              {meta.label}
            </span>
            <span
              className="font-mono tabular-nums"
              title={
                citationCount > 0
                  ? `${citationCount} dış kaynak bu varlığı destekliyor.`
                  : "Henüz dış kanıt (atıf) bulunamadı."
              }
            >
              · {citationCount} kaynak
            </span>
          </div>
          {openQuestions.length > 0 ? (
            <p className="mt-1 text-[11px] italic text-fg-subtle">
              {openQuestions[0]}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap gap-1 pl-8">
        {group.cells.map((c) => {
          const cm = STATUS_META[c.status];
          return (
            <span
              key={c.id}
              className={cn(
                "rounded-full border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider",
                cm.chip,
              )}
              title={`${c.aspect}: ${cm.label}`}
            >
              {c.aspect}
            </span>
          );
        })}
      </div>
    </li>
  );
}

export interface InvestigationPanelProps {
  plan: InvestigationPlan;
}

export function InvestigationPanel({ plan }: InvestigationPanelProps) {
  const groups = useMemo(() => groupByEntity(plan.cells), [plan.cells]);
  const pct = scorePct(plan.coverageScore);
  const mustCoverOpen = groups.filter(
    (g) => g.mustCover && aggregateStatus(g.cells) !== "covered" && aggregateStatus(g.cells) !== "no_evidence",
  ).length;
  const barColor =
    pct >= 85
      ? "from-emerald-500 via-emerald-400 to-teal-300"
      : pct >= 60
        ? "from-amber-500 via-amber-400 to-yellow-300"
        : "from-rose-500 via-rose-400 to-orange-300";

  return (
    <div className="flex flex-col gap-4">
      <header className="rounded-xl border border-white/[0.06] bg-surface-1/40 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-fg-strong">
            <Search className="h-4 w-4 text-accent-300" aria-hidden />
            Soruşturma Defteri
          </h3>
          <span className="font-mono text-2xl font-bold tabular-nums text-fg-strong">
            %{pct}
          </span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.08]">
          <div
            className={cn("h-full rounded-full bg-gradient-to-r", barColor)}
            style={{ width: `${pct}%` }}
            aria-hidden
          />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-fg-subtle">
          <span>{groups.length} aktör/varlık takip ediliyor</span>
          {mustCoverOpen > 0 ? (
            <span className="flex items-center gap-1 text-rose-300">
              <ShieldAlert className="h-3 w-3" aria-hidden />
              {mustCoverOpen} zorunlu varlık hâlâ açık
            </span>
          ) : (
            <span className="text-emerald-300">Tüm zorunlu varlıklar kapatıldı</span>
          )}
        </div>
      </header>

      <ul className="flex flex-col gap-2" aria-label="Soruşturma defteri varlık listesi">
        {groups.map((g) => (
          <EntityCard key={g.entity} group={g} />
        ))}
      </ul>
    </div>
  );
}

/**
 * InvestigationLiveBar — canlı transcript'in üstünde duran tek satırlık
 * kompakt soruşturma defteri göstergesi. `CoverageLiveBar` ile aynı
 * yerleşim/davranış sözleşmesi — ilk `investigation.plan` gelene kadar
 * hiç render edilmez.
 */
export function InvestigationLiveBar() {
  const plan = useDebateStore((s) => s.investigationPlan);
  if (!plan || plan.cells.length === 0) return null;

  const groups = groupByEntity(plan.cells);
  const mustCoverOpen = groups.filter(
    (g) => g.mustCover && aggregateStatus(g.cells) !== "covered" && aggregateStatus(g.cells) !== "no_evidence",
  );
  const pct = scorePct(plan.coverageScore);
  const title =
    mustCoverOpen.length > 0
      ? `Açık zorunlu varlıklar:\n${mustCoverOpen.map((g) => `○ ${g.entity}`).join("\n")}`
      : "Tüm zorunlu varlıklar kapatıldı.";

  return (
    <div
      className="flex items-center gap-2.5 border-b border-white/[0.06] px-4 py-2 sm:px-5"
      title={title}
      role="status"
      aria-label={`Soruşturma defteri: %${pct} kapsam`}
    >
      <Search className="h-3.5 w-3.5 flex-none text-accent-300" aria-hidden />
      <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-fg-subtle">
        Soruşturma
      </span>
      <div className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-white/[0.08]">
        <div
          className={cn(
            "h-full rounded-full bg-gradient-to-r",
            pct >= 85
              ? "from-emerald-500 to-teal-300"
              : pct >= 60
                ? "from-amber-500 to-yellow-300"
                : "from-rose-500 to-orange-300",
          )}
          style={{ width: `${pct}%` }}
          aria-hidden
        />
      </div>
      <span className="flex-none font-mono text-[10px] tabular-nums text-fg-subtle">
        <span className="font-medium text-fg-strong">{groups.length}</span> aktör
        {mustCoverOpen.length > 0 ? (
          <span className="text-rose-300"> · {mustCoverOpen.length} zorunlu açık</span>
        ) : null}
        <span className="ml-1.5 text-fg-subtle">%{pct}</span>
      </span>
    </div>
  );
}
